import { LoadInformationFieldNames } from '@contracts/order/fields/LoadInformationFields';
import { FloridaLoadDescription } from '@contracts/order/valueNormalizers/FloridaLoadDescription';
import { FloridaLoadIdentity } from '@contracts/order/valueNormalizers/FloridaLoadIdentity';
import { FloridaPermitType } from '@contracts/order/valueNormalizers/FloridaPermitType';
import { LoadDisposition } from '@contracts/order/valueNormalizers/LoadDisposition';
import { LoadIndustryType } from '@contracts/order/valueNormalizers/LoadIndustryType';
import { LoadingMethod } from '@contracts/order/valueNormalizers/LoadingMethod';
import {
  BooleanField,
  DropdownField,
  MultipleValuesField,
  PositiveFloatField,
  PositiveIntegerField,
  StringField,
} from '@src/permitField/PermitFields';
import * as yup from 'yup';

export class LoadDescriptionField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadDescription;
}

export class FloridaPermitTypeField extends DropdownField {
  public static fieldName = LoadInformationFieldNames.FloridaPermitType;
  public static options = Object.values(FloridaPermitType);
  public static validator = yup
    .string()
    .oneOf(FloridaPermitTypeField.options)
    .required();
}

export class FloridaLoadDescriptionField extends DropdownField {
  public static fieldName = LoadInformationFieldNames.FloridaLoadDescription;
  public static options = Object.values(FloridaLoadDescription);
  public static validator = yup
    .string()
    .oneOf(FloridaLoadDescriptionField.options)
    .required();
}

export class FloridaLoadIdentityField extends DropdownField {
  public static fieldName = LoadInformationFieldNames.FloridaLoadIdentity;
  public static options = Object.values(FloridaLoadIdentity);
  public static validator = yup
    .string()
    .oneOf(FloridaLoadIdentityField.options)
    .required();
}

export class CommodityField extends StringField {
  public static fieldName = LoadInformationFieldNames.Commodity;
}

export class LoadMakeField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadMake;
}

export class LoadModelField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadModel;
}

export class LoadSerialNumberField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadSerialNumber;
}

export class LoadIndustryTypeField extends DropdownField {
  public static fieldName = LoadInformationFieldNames.LoadIndustryType;
  public static options = Object.values(LoadIndustryType);
  public static validator = yup
    .string()
    .oneOf(LoadIndustryTypeField.options)
    .required();
}

export class LoadQuantityField extends PositiveIntegerField {
  public static fieldName = LoadInformationFieldNames.LoadQuantity;
}

export class LoadYearField extends PositiveIntegerField {
  public static fieldName = LoadInformationFieldNames.LoadYear;
}

export class LoadLoadingMethod extends DropdownField {
  public static fieldName = LoadInformationFieldNames.LoadLoadingMethod;
  public static options = Object.values(LoadingMethod);
  public static validator = yup
    .string()
    .oneOf(LoadLoadingMethod.options)
    .required();
}

export class LoadIsNewlyManufacturedField extends BooleanField {
  public static fieldName = LoadInformationFieldNames.LoadIsNewlyManufactured;
}

export class LoadIsSingleItemField extends BooleanField {
  public static fieldName = LoadInformationFieldNames.LoadIsSingleItem;
}

export class LoadDispositionField extends MultipleValuesField {
  public static fieldName = LoadInformationFieldNames.LoadDisposition;
  public static options = Object.values(LoadDisposition);
  public static validator = yup
    .array()
    .transform((value) => (!value ? undefined : value))
    .when(LoadIsSingleItemField.fieldName, {
      is: false,
      then: (schema) =>
        schema
          .ensure()
          .min(1, 'needsDisposition')
          .required()
          .of(
            yup
              .string()
              .oneOf(LoadDispositionField.options, 'needsDisposition'),
          ),
      otherwise: (schema) => schema.notRequired(),
    });
}

export class LoadSideBySideCountField extends PositiveIntegerField {
  public static fieldName = LoadInformationFieldNames.LoadSideBySideCount;
  public static validator = yup.number().when(LoadDispositionField.fieldName, {
    is: (values: unknown) =>
      Array.isArray(values) &&
      values.includes(LoadDisposition.loadIsSideBySide),
    then: (schema) =>
      schema.typeError('isNumber').integer().min(1, 'isPositive').required(),
    otherwise: (schema) =>
      schema
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional(),
  });
}

export class LoadEndToEndCountField extends PositiveIntegerField {
  public static fieldName = LoadInformationFieldNames.LoadEndToEndCount;
  public static validator = yup.number().when(LoadDispositionField.fieldName, {
    is: (values: unknown) =>
      Array.isArray(values) && values.includes(LoadDisposition.loadIsEndToEnd),
    then: (schema) =>
      schema.typeError('isNumber').integer().min(1, 'isPositive').required(),
    otherwise: (schema) =>
      schema
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional(),
  });
}

export class LoadStackCountField extends PositiveIntegerField {
  public static fieldName = LoadInformationFieldNames.LoadStackCount;
  public static validator = yup.number().when(LoadDispositionField.fieldName, {
    is: (values: unknown) =>
      Array.isArray(values) && values.includes(LoadDisposition.loadIsStacked),
    then: (schema) =>
      schema.typeError('isNumber').integer().min(1, 'isPositive').required(),
    otherwise: (schema) =>
      schema
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional(),
  });
}

export class LoadOwnerNameField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadOwnerName;
}

export class LoadOwnerAddressField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadOwnerAddress;
}

export class LoadLengthField extends PositiveFloatField {
  public static fieldName = LoadInformationFieldNames.LoadLength;
}

export class LoadWidthField extends PositiveFloatField {
  public static fieldName = LoadInformationFieldNames.LoadWidth;
}

export class LoadHeightField extends PositiveFloatField {
  public static fieldName = LoadInformationFieldNames.LoadHeight;
}

export class LoadWeightField extends PositiveFloatField {
  public static fieldName = LoadInformationFieldNames.LoadWeight;
}

export class LoadSealedContainerNumberField extends StringField {
  public static fieldName = LoadInformationFieldNames.LoadSealedContainerNumber;
}

export class LoadSealedContainerChassisNumberField extends StringField {
  public static fieldName =
    LoadInformationFieldNames.LoadSealedContainerChassisNumber;
}

export const loadInformationFields = {
  [LoadDescriptionField.fieldName]: LoadDescriptionField.validator,
  [FloridaPermitTypeField.fieldName]: FloridaPermitTypeField.validator,
  [FloridaLoadDescriptionField.fieldName]:
    FloridaLoadDescriptionField.validator,
  [FloridaLoadIdentityField.fieldName]: FloridaLoadIdentityField.validator,
  [CommodityField.fieldName]: CommodityField.validator,
  [LoadDispositionField.fieldName]: LoadDispositionField.validator,
  [LoadEndToEndCountField.fieldName]: LoadEndToEndCountField.validator,
  [LoadHeightField.fieldName]: LoadHeightField.validator,
  [LoadIndustryTypeField.fieldName]: LoadIndustryTypeField.validator,
  [LoadIsNewlyManufacturedField.fieldName]:
    LoadIsNewlyManufacturedField.validator,
  [LoadIsSingleItemField.fieldName]: LoadIsSingleItemField.validator,
  [LoadLengthField.fieldName]: LoadLengthField.validator,
  [LoadLoadingMethod.fieldName]: LoadLoadingMethod.validator,
  [LoadMakeField.fieldName]: LoadMakeField.validator,
  [LoadModelField.fieldName]: LoadModelField.validator,
  [LoadOwnerAddressField.fieldName]: LoadOwnerAddressField.validator,
  [LoadOwnerNameField.fieldName]: LoadOwnerNameField.validator,
  [LoadQuantityField.fieldName]: LoadQuantityField.validator,
  [LoadSealedContainerChassisNumberField.fieldName]:
    LoadSealedContainerChassisNumberField.validator,
  [LoadSealedContainerNumberField.fieldName]:
    LoadSealedContainerNumberField.validator,
  [LoadSerialNumberField.fieldName]: LoadSerialNumberField.validator,
  [LoadSideBySideCountField.fieldName]: LoadSideBySideCountField.validator,
  [LoadStackCountField.fieldName]: LoadStackCountField.validator,
  [LoadWeightField.fieldName]: LoadWeightField.validator,
  [LoadWidthField.fieldName]: LoadWidthField.validator,
  [LoadYearField.fieldName]: LoadYearField.validator,
};
